import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import LocationAutocomplete from '@/components/LocationAutocomplete';

export default function EditProjectDialog({
  open, onOpenChange,
  editProjectData, setEditProjectData,
  handleSaveProject, savingProject,
  project,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sm font-bold uppercase tracking-wider">Edit Project Information</DialogTitle>
          <DialogDescription>Update all project details below.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Project Name</Label>
              <Input data-testid="edit-project-name" value={editProjectData.name || ''} onChange={e => setEditProjectData(d => ({...d, name: e.target.value}))} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Client</Label>
              <Input value={project?.client_name || ''} disabled className="bg-slate-50 text-slate-500" />
              <p className="text-[10px] text-slate-400 mt-0.5">Client cannot be changed after creation</p>
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Description</Label>
            <Textarea data-testid="edit-project-desc" value={editProjectData.description || ''} onChange={e => setEditProjectData(d => ({...d, description: e.target.value}))} rows={3} />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Contract Type</Label>
              <Select value={editProjectData.contract_type || 'JBCC'} onValueChange={v => setEditProjectData(d => ({...d, contract_type: v}))}>
                <SelectTrigger data-testid="edit-project-contract-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="JBCC">JBCC</SelectItem>
                  <SelectItem value="NEC">NEC</SelectItem>
                  <SelectItem value="FIDIC">FIDIC</SelectItem>
                  <SelectItem value="GCC">GCC</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Contract Number</Label>
              <Input data-testid="edit-project-contract-num" value={editProjectData.contract_number || ''} onChange={e => setEditProjectData(d => ({...d, contract_number: e.target.value}))} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Project Number</Label>
              <Input data-testid="edit-project-num" value={editProjectData.project_number || ''} onChange={e => setEditProjectData(d => ({...d, project_number: e.target.value}))} />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Contractor Name</Label>
            <Input data-testid="edit-project-contractor" value={editProjectData.contractor_name || ''} onChange={e => setEditProjectData(d => ({...d, contractor_name: e.target.value}))} />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Location</Label>
            <LocationAutocomplete
              value={editProjectData.location}
              onChange={loc => setEditProjectData(d => ({...d, location: loc}))}
            />
            {editProjectData.location?.address && (
              <p className="text-xs text-emerald-600 mt-1">{editProjectData.location.address}</p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={handleSaveProject} disabled={savingProject} className="rounded-sm" data-testid="save-project-btn">
            {savingProject ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
